import { BackendError, Service, type BasketCustomerOrderEntity } from '@genial/services';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import { FilterModalTypes } from '../../../components/FilterModal';
import {
  CustomerItemDataProps,
  CustomerItemDataResponseProps,
  RemoveCustomerOfferBasketParams,
  UpdateCustomerOfferBasketParams,
} from '../../../pages/BatchTicket/utils/types';
import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  basketCustomerOrderResumeState,
  batchTicketCustomerFilterState,
  customerCartState,
  customerCartStatusState,
  productsCartState,
  tagsState,
} from '../atom';
import { customerParams, customerSearchTermParams } from '../constants';
import {
  filterCustomersWithErrorFreeOffers,
  filterCustomersWithOffersThatNeedAttention,
  normalizeCustomerData,
} from '../normalize';

export const addCustomerCartSelector = selector({
  key: 'addCustomerCartSelector',
  set: ({ set, get }, newCustomer) => {
    const currentCustomerCart = get(customerCartState);
    const updatedCustomerCart = [
      ...currentCustomerCart,
      newCustomer as unknown as CustomerItemDataProps,
    ];

    set(customerCartState, updatedCustomerCart);
  },
  get: ({ get }) => get(customerCartState),
});

export const addCustomerListCartSelector = selector({
  key: 'addCustomerListCartSelector',
  set: ({ set }, data) => {
    set(customerCartState, data as unknown as CustomerItemDataProps[]);
  },
  get: ({ get }) => get(customerCartState),
});

export const removeCustomerCartSelector = selector({
  key: 'removeCustomerCartSelector',
  set: ({ set, get }, customerToRemove) => {
    const currentCustomerCart = get(customerCartState);
    const customerRemoveId = (customerToRemove as unknown as CustomerItemDataProps).customerId;
    const updatedCustomerCart = currentCustomerCart.filter(
      (customerCart) => customerCart.customerId !== customerRemoveId
    );
    set(customerCartState, updatedCustomerCart);
  },
  get: ({ get }) => get(customerCartState),
});

export const customerCartCountSelector = selector({
  key: 'customerCartCountSelector',
  get: ({ get }) => {
    const customerCartList = get(customerCartState);
    return customerCartList.length;
  },
});

export const customerCartTotalAvailableBalanceSelector = selector({
  key: 'customerCartTotalAvailableBalanceSelector',
  get: ({ get }) => {
    const customerCartList = get(customerCartState);

    const total = customerCartList.reduce((acc, { availableBalance }) => {
      return availableBalance + acc;
    }, 0);

    return { total, totalFormatted: Utils.Mask.Money(total) };
  },
});

export const customerCartEstimatedBalanceSelector = selector({
  key: 'customerCartEstimatedBalanceSelector',
  get: ({ get }) => {
    const customerCartList = get(customerCartState);
    const {
      fixedIncome: { items: itemsFixedIncome },
      funds: { items: itemsFunds },
      coes: { items: itemsCoes },
      directTreasury: { items: itemsDirectTreasury },
      equity: { items: itemsEquity },
    } = get(productsCartState);

    const offers = [
      ...itemsFixedIncome,
      ...itemsFunds,
      ...itemsCoes,
      ...itemsDirectTreasury,
      ...itemsEquity,
    ];

    const offersTotal = offers.reduce((acc, current) => {
      return acc + (current?.orderValue || 0);
    }, 0);

    const customerAvailable = customerCartList.reduce((acc, { availableBalance }) => {
      return availableBalance + acc;
    }, 0);

    const total = customerAvailable - offersTotal;
    return { total, totalFormatted: Utils.Mask.Money(total) };
  },
});

export const customersQuery = selectorFamily<
  CustomerItemDataResponseProps | null | undefined,
  { page: number; searchTerm: string; tags?: string[] }
>({
  key: 'customersQuery',
  get: ({ page, searchTerm, tags }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const hasSearchTerm = searchTerm.length > 0;

        const params = {
          ...(hasSearchTerm ? customerSearchTermParams : customerParams),
          page,
          searchTerm,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          tags: tags || undefined,
        };

        const customersService = hasSearchTerm
          ? Service.Customer.V1.getBySearchTerm
          : Service.Customer.V1.getAllByFilter;

        const { data, meta } = await customersService(params);

        const normalizedData = normalizeCustomerData(data);

        // limit 40
        // data 40
        // data 100

        // limit 40
        // data 11

        const hasNext = hasSearchTerm ? false : normalizedData.length >= meta.pagination.limit;

        return {
          data: normalizedData,
          meta: { pagination: { ...meta.pagination, hasNext } },
        } as CustomerItemDataResponseProps;
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const updateCustomerCartStatusSelector = selector({
  key: 'updateCustomerCartStatusSelector',
  get: ({ get }) => get(customerCartStatusState),
  set: ({ set }, value) => set(customerCartStatusState, value),
});

export const updateBatchTicketFiltersSelector = selector({
  key: 'updateBatchTicketFiltersSelector',
  set: ({ set, get }) => {
    const currentBatchTicketCustomerFilter = get(batchTicketCustomerFilterState);
    const currentTagListCustomerFilter = get(tagsState).list;
    const existsTagIndex = currentBatchTicketCustomerFilter.availableFilters.findIndex(
      (filter) => filter.fieldName === 'tags'
    );

    const availableFilters = currentBatchTicketCustomerFilter.availableFilters;
    const tagFilters = {
      type: 'section',
      title: 'Tags',
      fieldName: 'tags',
      options: currentTagListCustomerFilter.map((tag) => ({
        label: tag.tagName,
        value: tag.tagId,
      })),
    } as FilterModalTypes.FilterType;

    const updatedAvailableFilters =
      existsTagIndex > -1
        ? availableFilters.filter((filter) => filter.fieldName !== 'tags')
        : [...availableFilters];

    set(batchTicketCustomerFilterState, {
      appliedFilters: currentBatchTicketCustomerFilter.appliedFilters,
      selectedFilters: currentBatchTicketCustomerFilter.selectedFilters,
      availableFilters: [...updatedAvailableFilters, tagFilters],
    });
  },
  get: ({ get }) => get(batchTicketCustomerFilterState),
});

export const removeCustomerOfferByBasketOrderResumeSelector = selector({
  key: 'removeCustomerOfferByBasketOrderResumeSelector',
  set: ({ set, get }, data) => {
    const { basketCustomerOfferId } = data as unknown as RemoveCustomerOfferBasketParams;
    const currentBasketCustomerOrderResumeData = get(basketCustomerOrderResumeState);

    const updatedData = currentBasketCustomerOrderResumeData?.data?.all.map((order) => {
      return {
        ...order,
        customerOffers: order?.customerOffers?.map((customerOffer) => {
          const basketOffers = customerOffer?.basketOffers?.filter(
            (basketOffer) => basketOffer.basketCustomerOfferId !== basketCustomerOfferId
          );

          const totalMarketAmount = basketOffers?.reduce((acc, offer) => acc + offer.amount, 0);

          const totalMarketRequestedAmount = basketOffers?.reduce(
            (acc, offer) => acc + offer.requestedAmount,
            0
          );

          return {
            ...customerOffer,
            totalMarketAmount,
            totalMarketRequestedAmount,
            basketOffers,
          };
        }),
      };
    }) as BasketCustomerOrderEntity[];

    set(basketCustomerOrderResumeState, {
      ...currentBasketCustomerOrderResumeData,
      data: {
        all: updatedData,
        errors: filterCustomersWithOffersThatNeedAttention(updatedData),
        valid: filterCustomersWithErrorFreeOffers(updatedData),
      },
    });
  },
  get: ({ get }) => get(basketCustomerOrderResumeState),
});

export const updateCustomerOfferByBasketOrderResumeSelector = selector({
  key: 'updateCustomerOfferByBasketOrderResumeSelector',
  set: ({ set, get }, data) => {
    const { basketCustomerOfferId, ...updatedProperties } =
      data as unknown as UpdateCustomerOfferBasketParams;
    const currentBasketCustomerOrderResumeData = get(basketCustomerOrderResumeState);

    const updatedData = currentBasketCustomerOrderResumeData.data.all.map((basketItem) => {
      return {
        ...basketItem,
        customerOffers: basketItem?.customerOffers?.map((customerOffer) => {
          return {
            ...customerOffer,
            basketOffers: customerOffer?.basketOffers?.map((offer) => {
              if (offer.basketCustomerOfferId === basketCustomerOfferId) {
                return {
                  ...offer,
                  ...updatedProperties,
                };
              }
              return offer;
            }),
          };
        }),
      };
    });

    const updateTotals = updatedData.map((order) => {
      return {
        ...order,
        customerOffers: order?.customerOffers?.map((customerOffer) => {
          return {
            ...customerOffer,
            totalMarketAmount: customerOffer?.basketOffers?.reduce(
              (acc, offer) => acc + offer.amount,
              0
            ),
            totalMarketRequestedAmount: customerOffer?.basketOffers?.reduce(
              (acc, offer) => acc + offer.requestedAmount,
              0
            ),
          };
        }),
      };
    }) as BasketCustomerOrderEntity[];

    set(basketCustomerOrderResumeState, {
      ...currentBasketCustomerOrderResumeData,
      data: {
        all: updateTotals,
        errors: filterCustomersWithOffersThatNeedAttention(updateTotals),
        valid: filterCustomersWithErrorFreeOffers(updateTotals),
      },
    });
  },
  get: ({ get }) => get(basketCustomerOrderResumeState),
});
